import { LabelQueuesQuery } from "@dashboard/graphql";
import { Box, Button, Text } from "@saleor/macaw-ui-next";
import { ListPlus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useIntl } from "react-intl";

import { messages } from "./views/LabelPrintingOverview/messages";

export type SavedLabelQueue = NonNullable<LabelQueuesQuery["labelQueues"]>[number];

interface LabelSavedQueuesProps {
  queues: SavedLabelQueue[];
  loading: boolean;
  deletingId: string | null;
  // The queue whose products are being fetched after Open/Add.
  loadingId: string | null;
  onOpen: (queue: SavedLabelQueue) => void;
  onAppend: (queue: SavedLabelQueue) => void;
  onDelete: (queue: SavedLabelQueue) => void;
}

export const LabelSavedQueues = ({
  queues,
  loading,
  deletingId,
  loadingId,
  onOpen,
  onAppend,
  onDelete,
}: LabelSavedQueuesProps) => {
  const intl = useIntl();
  // Deleting is shared with every colleague, so it takes a second click.
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  if (queues.length === 0) {
    return (
      <Text size={2} color="default2" padding={3}>
        {intl.formatMessage(loading ? messages.savedQueuesLoading : messages.savedQueuesEmpty)}
      </Text>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      borderWidth={1}
      borderStyle="solid"
      borderColor="default1"
      borderRadius={3}
      data-test-id="saved-label-queues"
    >
      {queues.map((queue, index) => (
        <Box
          key={queue.id}
          display="flex"
          alignItems="center"
          gap={3}
          paddingX={3}
          paddingY={2}
          borderTopWidth={index === 0 ? 0 : 1}
          borderTopStyle="solid"
          borderColor="default1"
          flexWrap="wrap"
        >
          <Box __flex="1 1 220px" style={{ minWidth: 0 }}>
            <Text size={2} fontWeight="bold" display="block">
              {queue.name}
            </Text>
            <Text size={1} color="default2">
              {intl.formatMessage(messages.savedQueueSummary, {
                labels: queue.labelCount,
                variants: queue.items.length,
              })}
              {queue.createdByName ? ` · ${queue.createdByName}` : ""}
              {" · "}
              {intl.formatDate(queue.updatedAt, { dateStyle: "short", timeStyle: "short" })}
            </Text>
          </Box>
          {confirmingId === queue.id ? (
            <Box display="flex" alignItems="center" gap={2}>
              <Text size={2}>
                {intl.formatMessage(messages.savedQueueDeleteConfirm, { name: queue.name })}
              </Text>
              <Button variant="secondary" size="small" onClick={() => setConfirmingId(null)}>
                {intl.formatMessage(messages.cancel)}
              </Button>
              <Button
                variant="error"
                size="small"
                disabled={deletingId === queue.id}
                onClick={() => onDelete(queue)}
                data-test-id="confirm-delete-queue"
              >
                {intl.formatMessage(messages.savedQueueDelete)}
              </Button>
            </Box>
          ) : (
            <Box display="flex" alignItems="center" gap={1}>
              <Button
                variant="tertiary"
                size="small"
                icon={<ListPlus size={14} />}
                title={intl.formatMessage(messages.savedQueueAppendHint)}
                disabled={loadingId !== null}
                onClick={() => onAppend(queue)}
              >
                {intl.formatMessage(messages.savedQueueAppend)}
              </Button>
              <Button
                variant="secondary"
                size="small"
                disabled={loadingId !== null}
                onClick={() => onOpen(queue)}
                data-test-id="open-queue"
              >
                {intl.formatMessage(messages.savedQueueOpen)}
              </Button>
              {queue.canEdit && (
                <Button
                  variant="tertiary"
                  size="small"
                  icon={<Trash2 size={14} />}
                  aria-label={intl.formatMessage(messages.savedQueueDeleteNamed, {
                    name: queue.name,
                  })}
                  title={intl.formatMessage(messages.savedQueueDelete)}
                  onClick={() => setConfirmingId(queue.id)}
                  data-test-id="delete-queue"
                />
              )}
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
};
